import React, { useState, useEffect } from 'react'
import { Routes, Route, NavLink, Navigate, useNavigate } from 'react-router-dom'
import { 
  LayoutDashboard, 
  Bot, 
  GitBranch, 
  Activity, 
  Settings,
  Zap,
  Menu,
  X,
  Lock,
  LogOut,
  Cpu
} from 'lucide-react'
import Dashboard from './pages/Dashboard'
import Agents from './pages/Agents'
import NeuralMap from './pages/NeuralMap'
import Workflows from './pages/Workflows'
import ActivityPage from './pages/Activity'
import Skills from './pages/Skills'
import API_BASE from './config'

// Login Component
function Login({ onLogin }) {
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    
    try {
      const response = await fetch(`${API_BASE}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password })
      })
      
      const data = await response.json()
      
      if (data.success) {
        localStorage.setItem('auth_token', data.token)
        onLogin()
      } else {
        setError('Invalid password')
      }
    } catch (err) {
      setError('Login failed. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="card p-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-br from-accent to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold mb-2">SISTL Command Center</h1>
            <p className="text-text-secondary">Enter password to continue</p>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                className="w-full px-4 py-3 bg-surface border border-border rounded-lg focus:border-accent focus:outline-none transition-colors"
                autoFocus
              />
            </div>
            
            {error && (
              <div className="text-danger text-sm text-center">{error}</div>
            )}
            
            <button
              type="submit"
              disabled={loading || !password}
              className="w-full py-3 bg-accent hover:bg-accent/90 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  <span>Logging in...</span>
                </>
              ) : (
                <>
                  <Lock className="w-5 h-5" />
                  <span>Sign In</span>
                </>
              )}
            </button>
          </form>
          
          <div className="mt-6 text-center text-xs text-text-muted">
            <p>Protected Area • Authorized Personnel Only</p>
          </div>
        </div>
      </div>
    </div>
  )
}

function App() {
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const [wsConnected, setWsConnected] = useState(false)
  const [systemStatus, setSystemStatus] = useState('initializing')
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return !!localStorage.getItem('auth_token')
  })
  const navigate = useNavigate()

  useEffect(() => {
    if (!isAuthenticated) return
    
    // WebSocket connection
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    const wsUrl = window.location.protocol === 'https:' 
      ? `${wsProtocol}//${window.location.hostname}/ws`
      : `${wsProtocol}//${window.location.hostname}:${window.location.port || '3457'}/ws`
    
    const ws = new WebSocket(wsUrl)
    
    ws.onopen = () => {
      console.log('🔌 WebSocket connected')
      setWsConnected(true)
      setSystemStatus('online')
    }
    
    ws.onclose = () => {
      console.log('🔌 WebSocket disconnected')
      setWsConnected(false)
      setSystemStatus('offline')
    }
    
    ws.onerror = (error) => {
      console.error('WebSocket error:', error)
      setWsConnected(false)
      setSystemStatus('error')
    }
    
    return () => ws.close()
  }, [isAuthenticated])

  const handleLogin = () => {
    setIsAuthenticated(true)
    navigate('/')
  }

  const handleLogout = () => {
    localStorage.removeItem('auth_token')
    setIsAuthenticated(false)
    navigate('/login')
  }

  // Protected Route wrapper
  const ProtectedRoute = ({ children }) => {
    if (!isAuthenticated) {
      return <Navigate to="/login" replace />
    }
    return children
  }

  if (!isAuthenticated) {
    return <Login onLogin={handleLogin} />
  }

  const navItems = [
    { path: '/', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/agents', icon: Bot, label: 'Agents (178)' },
    { path: '/skills', icon: Cpu, label: 'Skills (43)' },
    { path: '/map', icon: GitBranch, label: 'Neural Map' },
    { path: '/workflows', icon: Zap, label: 'Workflows' },
    { path: '/activity', icon: Activity, label: 'Activity' },
  ]

  return (
    <div className="flex h-screen bg-background text-text-primary overflow-hidden">
      {/* Sidebar */}
      <aside 
        className={`${sidebarOpen ? 'w-64' : 'w-0'} 
                   bg-surface border-r border-border 
                   transition-all duration-300 flex flex-col
                   ${sidebarOpen ? '' : 'overflow-hidden'}`}
      >
        {/* Logo */}
        <div className="h-16 flex items-center px-4 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-gradient-to-br from-accent to-purple-600 rounded-lg flex items-center justify-center">
              <Zap className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-lg">SISTL</h1>
              <p className="text-xs text-text-secondary">AI Command Center</p>
            </div>
          </div>
        </div>
        
        {/* Navigation */}
        <nav className="flex-1 py-4 px-2 space-y-1">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) => 
                `nav-link ${isActive ? 'active' : ''}`
              }
            >
              <item.icon className="w-5 h-5" />
              <span>{item.label}</span>
            </NavLink>
          ))}
          
          <button
            onClick={handleLogout}
            className="nav-link w-full text-left text-danger hover:text-danger"
          >
            <LogOut className="w-5 h-5" />
            <span>Logout</span>
          </button>
        </nav>
        
        {/* Status Footer */}
        <div className="p-4 border-t border-border">
          <div className="flex items-center justify-between text-sm">
            <span className="text-text-secondary">System Status</span>
            <div className="flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full ${
                wsConnected ? 'bg-success animate-pulse' : 'bg-danger'
              }`} />
              <span className={wsConnected ? 'text-success' : 'text-danger'}>
                {wsConnected ? 'Online' : 'Offline'}
              </span>
            </div>
          </div>
          <p className="text-xs text-text-muted mt-2">
            v2.0 • 178 Agents • 43 Skills
          </p>
        </div>
      </aside>
      
      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <header className="h-16 bg-surface border-b border-border flex items-center justify-between px-4">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 hover:bg-surface-elevated rounded-lg transition-colors"
          >
            {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
          
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-sm font-medium">AI Team Dashboard</p>
              <p className="text-xs text-text-secondary">
                {new Date().toLocaleString('en-US', { 
                  weekday: 'short', 
                  month: 'short', 
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </p>
            </div>
            <div className="w-8 h-8 bg-accent/10 rounded-full flex items-center justify-center border border-accent/20">
              <span className="text-accent font-bold text-sm">S</span>
            </div>
          </div>
        </header>
        
        {/* Page Content */}
        <main className="flex-1 overflow-auto p-6">
          <Routes>
            <Route path="/login" element={
              isAuthenticated ? <Navigate to="/" replace /> : <Login onLogin={handleLogin} />
            } />
            <Route path="/" element={
              <ProtectedRoute><Dashboard /></ProtectedRoute>
            } />
            <Route path="/agents" element={
              <ProtectedRoute><Agents /></ProtectedRoute>
            } />
            <Route path="/skills" element={
              <ProtectedRoute><Skills /></ProtectedRoute>
            } />
            <Route path="/map" element={
              <ProtectedRoute><NeuralMap /></ProtectedRoute>
            } />
            <Route path="/workflows" element={
              <ProtectedRoute><Workflows /></ProtectedRoute>
            } />
            <Route path="/activity" element={
              <ProtectedRoute><ActivityPage /></ProtectedRoute>
            } />
          </Routes>
        </main>
      </div>
    </div>
  )
}

export default App
